import {MDIntent} from '@mintdata-wsp/common/components/MDIntent';
import {AnalyticsEventActions} from '@mintdata-wsp/common/controllers/analytics/AnalyticsEventActions';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {ModifyDesignRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ModifyDesignRequest';
import {RenameDesignRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/RenameDesignRequest';
import {ReorderDesignRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ReorderDesignRequest';
import {DesignResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/DesignResponse';
import {DesignsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/DesignsResponse';
import {DesignWithTimestampsResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/DesignWithTimestampsResponse';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {Design, DesignId, Project} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {ToastData} from '@mintdata-wsp/common/state/ui/visualDesigner/ToastData';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {DesignMovedToast} from '@mintdata-wsp/wsp/ui/components/toasts/DesignMovedToast';
import {TextInputDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/TextInputDialog';
import bind from 'bind-decorator';
import {action, runInAction} from 'mobx';
import * as React from 'react';

export class DesignsController
{
    @action.bound
    public async createNewDesign(project:Project, name:string):Promise<Design>
    {
        const req:ModifyDesignRequest = new ModifyDesignRequest(name, '');
        const resp = await API.designs.create(project.id, req) as DesignResponse | ApiError;

        if( ApiError.isError(resp) )
            throw (resp as ApiError).message;

        // const initialPatches:IJsonPatch[] = DesignState.getInitialPatches();
        //
        // return hippoAPI.designState.updateDesign(
        //     UniqueId.fromString((resp as DesignResponse).uuid),
        //     0,
        //     initialPatches
        // );

        const designs = await controllers.data.designs.getDesignsForProject(project) as Design[] | ApiError;

        if( ApiError.isError(designs) )
            throw (designs as ApiError).message;

        controllers.ui.mainPage.setCurrentDesignsIfCurrentProject(project, designs as Design[]);

        return Design.fromResponse(resp as DesignWithTimestampsResponse);
    }

    public async generateNewDesignName():Promise<string>
    {
        const designs:Design[] = rootState.ui.mainPage.currentDesigns;
        const existingNames:string[] = designs.map(({name}) => name.toLowerCase());

        const initialName = 'Untitled Design';
        let newName:string;
        let index = 1;

        while( true )
        {
            newName = index === 1 ? initialName : `${initialName} ${index}`;

            if( existingNames.indexOf(newName.toLowerCase()) === -1 )
                break;

            index += 1;
        }

        return newName;
    }

    public deleteDesignDialog(project:Project, design:Design):Promise<void>
    {
        return new Promise((resolve, reject) =>
            {
                controllers.ui.dialogs.showDangerConfirm(`Delete design "${design.name}"?`, `This operation cannot be undone.`,
                () =>
                {
                    controllers.data.designs.deleteDesign(project, design)
                        .then(() => resolve())
                        .catch((error) => reject(error));
                },
                [null, 'Delete design']
                );
            })
    }

    @action.bound
    public deleteDesign(project:Project, design:Design):Promise<void>
    {
        this._addToDeletedDesignsIds(design.id);

        return new Promise((resolve, reject) =>
        {
            API.designs.delete(project.id, design.id)
                .then(action(() =>
                {
                    this.getDesignsForProject(project).then((designsMinusOne:Design[]) =>
                    {
                        controllers.ui.mainPage.setCurrentDesignsIfCurrentProject(project, designsMinusOne);
                        this._removeFromDeletedDesignsIds(design.id);
                        resolve();
                    });
                }));
        });
    }

    @bind
    public reorderDesign(project:Project, design:Design, newOrderIndex:number):void
    {
        API.designs.reorder(project.id, design.id, new ReorderDesignRequest(newOrderIndex))
            .then(resp => this.getDesignsForProject(project))
            .then(designs => controllers.ui.mainPage.setCurrentDesignsIfCurrentProject(project, designs));
    }

    @action.bound
    public moveDesign(thisProject:Project, design:Design, otherProject:Project):void
    {
        const {mainPage, toasts} = controllers.ui;

        this._addToDeletedDesignsIds(design.id);

        let originalDesignName = design.name;
        let newDesignName = design.name;
        API.designs.move(thisProject.id, otherProject.id, design.id)
            .then(resp =>
            {
                newDesignName = resp.name;
                return this.getDesignsForProject(thisProject);
            })
            .then(designs =>
            {
                mainPage.setCurrentDesignsIfCurrentProject(thisProject, designs);
                this._removeFromDeletedDesignsIds(design.id);

                const originalOrderIndex = design.orderIndex;
                const onUndo = () =>
                {
                    this._undoMoveDesign(thisProject, design, otherProject, originalDesignName, originalOrderIndex);
                    toasts.hideToast(toastData.id);
                };

                const onClose = () =>
                {
                    toasts.hideToast(toastData.id);
                };

                const toastData = new ToastData(() =>
                    <DesignMovedToast
                        srcProjectName={thisProject.name}
                        destProjectName={otherProject.name}
                        originalDesignName={originalDesignName}
                        newDesignName={newDesignName}
                        onUndo={onUndo}
                        onClose={onClose}
                    />);
                toasts.showToast(toastData);

                const {currentOrganization} = rootState.userSession;

                controllers.analytics.main.trackEvent(
                    AnalyticsEventActions.WSP_DESIGN_MOVED,
                    {
                        org_sid: currentOrganization.siteId,
                        org_name: currentOrganization.name,

                        space_id: otherProject.id,
                        space_name: otherProject.name,

                        design_id: design.id,
                        design_name: design.name
                    }
                );

                return this.getDesignsForProject(otherProject);
            })
            .then(designs =>
            {
                runInAction(() =>
                {
                    otherProject.designs = designs;
                });
            });
    }

    @bind
    private _undoMoveDesign(thisProject:Project, design:Design, otherProject:Project, originalDesignName:string, originalOrderIndex:number):void
    {
        API.designs.move(otherProject.id, thisProject.id, design.id)
            .then(resp =>
                originalDesignName == resp.name
                    ? resp
                    : this.renameDesign(thisProject, design, originalDesignName)
            )
            .then(() => this.reorderDesign(thisProject, design, originalOrderIndex));
    }

    @action.bound
    private _addToDeletedDesignsIds(designId:DesignId)
    {
        rootState.ui.mainPage.deletedDesignsIds.push(designId);
    }

    @action.bound
    private _removeFromDeletedDesignsIds(designId:DesignId)
    {
        const {deletedDesignsIds} = rootState.ui.mainPage;

        const index = deletedDesignsIds.indexOf(designId);

        if( index === -1 ) return;

        deletedDesignsIds.splice(index, 1);
    }

    @action.bound
    public renameDesign(project:Project, design:Design, newName:string):Promise<any>
    {
        this._setRenameDesignServerError(null);

        return API.designs.rename(project.id, design.id, new RenameDesignRequest(newName))
            .then((resp) =>
            {
                if( ApiError.isError(resp) )
                {
                    this._setRenameDesignServerError((resp as ApiError).message);
                    throw resp;
                }

                return this.getDesignsForProject(project);
            })
            .then((designs:Design[] | ApiError) =>
            {
                if( ApiError.isError(designs) )
                {
                    throw (designs as ApiError).message;
                }

                controllers.ui.mainPage.setCurrentDesignsIfCurrentProject(project, designs as Design[]);
            });
    }

    @action.bound
    public async duplicateDesign(project:Project, design:Design):Promise<Design>
    {
        const resp:DesignWithTimestampsResponse = await API.designs.copy(project.id, design.id);

        const designs:Design[] = await this.getDesignsForProject(project);

        controllers.ui.mainPage.setCurrentDesignsIfCurrentProject(project, designs);

        return Design.fromResponse(resp);
    }

    @bind
    public getDesign(project:Project, id:DesignId):Promise<Design>
    {
        return API.designs.get(project.id, id).then(Design.fromResponse);
    }

    @bind
    public getDesignsForProject(project:Project):Promise<Design[]>
    {
        return new Promise((resolve, reject) =>
        {
            API.designs.list(project.id)
                .then(action((dr:DesignsResponse) =>
                {
                    let designs:Design[] = dr.designs.map((resp:DesignWithTimestampsResponse) =>
                    {
                        return Design.fromResponse(resp);
                    });

                    resolve(designs);
                }));
        });
    }

    @bind
    public renameDesignDialog(project:Project, design:Design):void
    {
        const props = new TextInputDialogProps(
            MDIntent.Default,
            `Rename design "${design.name}"`,
            null,
            null,
            'Enter new name',
            design.name,
            null,
            [null, 'Rename design'],
            this._onConfirmedRenameDesign(project, design),
            null,
            true,
            this._onChangeRenameDesign,
            this._getErrorMessage
        );

        this._setRenameDesignServerError(null);
        controllers.ui.dialogs.showDialog(Dialogs.TextInput, false, props);
    }

    private _getErrorMessage():string
    {
        return rootState.ui.mainPage.renameDesign.errorMessage;
    }

    @bind
    private _onChangeRenameDesign(newValue:string)
    {
        this._setRenameDesignServerError(null);
    }

    private _onConfirmedRenameDesign(project:Project, design:Design)
    {
        return (newInputValue:string) =>
        {
            if( !newInputValue )
                return false;

            controllers.data.designs.renameDesign(project, design, newInputValue.trim())
                .then(() =>
                {
                    controllers.ui.dialogs.hideDialog(Dialogs.TextInput);

                    const {currentOrganization} = rootState.userSession;

                    controllers.analytics.main.trackEvent(
                        AnalyticsEventActions.WSP_DESIGN_RENAMED,
                        {
                            org_sid: currentOrganization.siteId,
                            org_name: currentOrganization.name,

                            space_id: project.id,
                            space_name: project.name,

                            design_id: design.id,
                            design_name: newInputValue
                        }
                    );
                })
                .catch(err =>
                {
                    console.warn(err);
                });

            return false; // don't hide TextInput Dialog
        };
    }

    @action
    private _setRenameDesignServerError(errorMessage:string)
    {
        rootState.ui.mainPage.renameDesign.errorMessage = errorMessage;
    }
}
